// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Teeter — deterministic maze generation. THIS IS THE WHOLE P2P-SYNC SURFACE: a maze
// is a pure function of (mode, seed), so every peer in a same-seed race builds the
// byte-identical board and no maze data ever crosses the wire.
//
// Grid model: a maze of cols x rows ROOMS is stored at double resolution as a
// (2*cols+1) x (2*rows+1) grid of cells, each WALL or FLOOR. Room (i,j) lives at grid
// (2i+1, 2j+1); the cell between two rooms is the wall that a carve opens. The ball
// moves in continuous grid-unit space where cell (gx,gy) occupies [gx,gx+1]x[gy,gy+1].
//
// Solvability is guaranteed BY CONSTRUCTION, not hoped for: a randomized-DFS perfect
// maze (every room reachable) is braided for route choice, then pits are placed only
// OFF a chosen shortest start->exit path, and gems only on cells reachable while
// avoiding pits. tests/maze + tests/balance assert all of this over hundreds of seeds.

import { makeRng, type Rng } from '@ben-gy/game-engine/rng';
import type { Mode } from './modes';

export interface Cell {
  gx: number;
  gy: number;
}

export interface Maze {
  cols: number;
  rows: number;
  /** Grid dimensions: gw = 2*cols+1, gh = 2*rows+1. */
  gw: number;
  gh: number;
  /** gw*gh, row-major. 1 = wall, 0 = floor. */
  solid: Uint8Array;
  start: Cell;
  exit: Cell;
  holes: Cell[];
  gems: Cell[];
}

export const roomToGrid = (i: number, j: number): Cell => ({ gx: 2 * i + 1, gy: 2 * j + 1 });

const idx = (m: { gw: number }, gx: number, gy: number): number => gy * m.gw + gx;

/** True when (gx,gy) is a wall OR out of bounds — the ball collides with both. */
export function solidAt(m: Maze, gx: number, gy: number): boolean {
  if (gx < 0 || gy < 0 || gx >= m.gw || gy >= m.gh) return true;
  return m.solid[idx(m, gx, gy)] === 1;
}

/** Centre of grid cell (gx,gy) in world (grid-unit) space. */
export const cellCenter = (c: Cell): { x: number; y: number } => ({ x: c.gx + 0.5, y: c.gy + 0.5 });

// Fixed neighbour order → deterministic DFS/BFS.
const DIRS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [0, 1],
  [-1, 0],
  [0, -1],
];

/** Shuffle in place with the seeded rng (Fisher–Yates). */
function shuffle<T>(arr: T[], rng: Rng): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const t = arr[i];
    arr[i] = arr[j];
    arr[j] = t;
  }
}

/** Room-graph neighbours actually connected by an open wall cell. */
function openRoomNeighbours(m: Maze, i: number, j: number): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  const g = roomToGrid(i, j);
  for (const [dx, dy] of DIRS) {
    const ni = i + dx;
    const nj = j + dy;
    if (ni < 0 || nj < 0 || ni >= m.cols || nj >= m.rows) continue;
    if (!solidAt(m, g.gx + dx, g.gy + dy)) out.push([ni, nj]);
  }
  return out;
}

/** BFS over FLOOR grid cells; returns one shortest path from a to b (inclusive). */
function shortestPath(m: Maze, a: Cell, b: Cell, blocked?: Set<number>): Cell[] | null {
  const prev = new Int32Array(m.gw * m.gh).fill(-1);
  const seen = new Uint8Array(m.gw * m.gh);
  const q: number[] = [];
  const s = idx(m, a.gx, a.gy);
  const t = idx(m, b.gx, b.gy);
  seen[s] = 1;
  q.push(s);
  while (q.length) {
    const cur = q.shift()!;
    if (cur === t) break;
    const cx = cur % m.gw;
    const cy = (cur / m.gw) | 0;
    for (const [dx, dy] of DIRS) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (solidAt(m, nx, ny)) continue;
      const ni = idx(m, nx, ny);
      if (seen[ni] || blocked?.has(ni)) continue;
      seen[ni] = 1;
      prev[ni] = cur;
      q.push(ni);
    }
  }
  if (!seen[t]) return null;
  const path: Cell[] = [];
  for (let cur = t; cur !== -1; cur = prev[cur]) {
    path.push({ gx: cur % m.gw, gy: (cur / m.gw) | 0 });
    if (cur === s) break;
  }
  return path.reverse();
}

/** All FLOOR cells reachable from `from`, optionally treating some indices as blocked. */
function reachable(m: Maze, from: Cell, blocked?: Set<number>): Set<number> {
  const seen = new Set<number>();
  const start = idx(m, from.gx, from.gy);
  seen.add(start);
  const q = [start];
  while (q.length) {
    const cur = q.shift()!;
    const cx = cur % m.gw;
    const cy = (cur / m.gw) | 0;
    for (const [dx, dy] of DIRS) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (solidAt(m, nx, ny)) continue;
      const ni = idx(m, nx, ny);
      if (seen.has(ni) || blocked?.has(ni)) continue;
      seen.add(ni);
      q.push(ni);
    }
  }
  return seen;
}

/** Grid (Manhattan-ish) BFS distance in ROOM steps from start room to every room. */
function roomDistances(m: Maze, startI: number, startJ: number): number[] {
  const dist = new Array<number>(m.cols * m.rows).fill(-1);
  const rIdx = (i: number, j: number): number => j * m.cols + i;
  dist[rIdx(startI, startJ)] = 0;
  const q: Array<[number, number]> = [[startI, startJ]];
  while (q.length) {
    const [i, j] = q.shift()!;
    for (const [ni, nj] of openRoomNeighbours(m, i, j)) {
      if (dist[rIdx(ni, nj)] === -1) {
        dist[rIdx(ni, nj)] = dist[rIdx(i, j)] + 1;
        q.push([ni, nj]);
      }
    }
  }
  return dist;
}

export function buildMaze(mode: Mode, seed: number): Maze {
  const rng = makeRng(seed);
  const cols = mode.cols;
  const rows = mode.rows;
  const gw = 2 * cols + 1;
  const gh = 2 * rows + 1;
  const solid = new Uint8Array(gw * gh).fill(1);
  const m: Maze = { cols, rows, gw, gh, solid, start: roomToGrid(0, 0), exit: roomToGrid(0, 0), holes: [], gems: [] };

  // Open every room cell.
  for (let j = 0; j < rows; j++) for (let i = 0; i < cols; i++) solid[idx(m, 2 * i + 1, 2 * j + 1)] = 0;

  // Randomized-DFS perfect maze.
  const visited = new Uint8Array(cols * rows);
  const rIdx = (i: number, j: number): number => j * cols + i;
  const stack: Array<[number, number]> = [[0, 0]];
  visited[rIdx(0, 0)] = 1;
  while (stack.length) {
    const [i, j] = stack[stack.length - 1];
    const cand: Array<[number, number]> = [];
    for (const [dx, dy] of DIRS) {
      const ni = i + dx;
      const nj = j + dy;
      if (ni < 0 || nj < 0 || ni >= cols || nj >= rows) continue;
      if (!visited[rIdx(ni, nj)]) cand.push([dx, dy]);
    }
    if (cand.length === 0) {
      stack.pop();
      continue;
    }
    shuffle(cand, rng);
    const [dx, dy] = cand[0];
    const g = roomToGrid(i, j);
    solid[idx(m, g.gx + dx, g.gy + dy)] = 0; // carve the wall between
    visited[rIdx(i + dx, j + dy)] = 1;
    stack.push([i + dx, j + dy]);
  }

  // Braid: open a fraction of dead-ends into loops (route choice).
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      if (openRoomNeighbours(m, i, j).length !== 1) continue;
      if (rng() >= mode.braid) continue;
      const walls: Array<[number, number]> = [];
      const g = roomToGrid(i, j);
      for (const [dx, dy] of DIRS) {
        const ni = i + dx;
        const nj = j + dy;
        if (ni < 0 || nj < 0 || ni >= cols || nj >= rows) continue;
        if (solidAt(m, g.gx + dx, g.gy + dy)) walls.push([dx, dy]);
      }
      if (walls.length === 0) continue;
      shuffle(walls, rng);
      const [dx, dy] = walls[0];
      solid[idx(m, g.gx + dx, g.gy + dy)] = 0;
    }
  }

  // Start bottom-left-ish; exit at the room farthest away (a long, varied route).
  m.start = roomToGrid(0, 0);
  const dist = roomDistances(m, 0, 0);
  let best = -1;
  let exitI = cols - 1;
  let exitJ = rows - 1;
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      const d = dist[rIdx(i, j)];
      if (d > best) {
        best = d;
        exitI = i;
        exitJ = j;
      }
    }
  }
  m.exit = roomToGrid(exitI, exitJ);

  // The safe route: one shortest path start->exit over floor cells. Pits go OFF it.
  const safe = shortestPath(m, m.start, m.exit) ?? [];
  const safeSet = new Set(safe.map((c) => idx(m, c.gx, c.gy)));

  // Collect candidate ROOM cells (not start/exit, off the safe path).
  const roomCells: Cell[] = [];
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      const g = roomToGrid(i, j);
      const id = idx(m, g.gx, g.gy);
      if (id === idx(m, m.start.gx, m.start.gy) || id === idx(m, m.exit.gx, m.exit.gy)) continue;
      if (safeSet.has(id)) continue;
      roomCells.push(g);
    }
  }
  shuffle(roomCells, rng);

  // Pits: greedy spread (min 2 room-steps apart), all off the safe path → solvable.
  const chosen: Cell[] = [];
  const farEnough = (c: Cell, list: Cell[], min: number): boolean =>
    list.every((o) => Math.abs(o.gx - c.gx) + Math.abs(o.gy - c.gy) >= min * 2);
  for (const c of roomCells) {
    if (chosen.length >= mode.holes) break;
    if (farEnough(c, chosen, 2)) chosen.push(c);
  }
  // Top up if spacing was too strict to reach the target count.
  for (const c of roomCells) {
    if (chosen.length >= mode.holes) break;
    if (!chosen.some((o) => o.gx === c.gx && o.gy === c.gy)) chosen.push(c);
  }
  m.holes = chosen.slice(0, mode.holes);

  // Gems: on cells reachable from start while AVOIDING pits, spread out.
  if (mode.gems > 0) {
    const blocked = new Set(m.holes.map((h) => idx(m, h.gx, h.gy)));
    const reach = reachable(m, m.start, blocked);
    const gemCand: Cell[] = [];
    for (let j = 0; j < rows; j++) {
      for (let i = 0; i < cols; i++) {
        const g = roomToGrid(i, j);
        const id = idx(m, g.gx, g.gy);
        if (id === idx(m, m.start.gx, m.start.gy) || id === idx(m, m.exit.gx, m.exit.gy)) continue;
        if (!reach.has(id)) continue;
        if (blocked.has(id)) continue;
        gemCand.push(g);
      }
    }
    shuffle(gemCand, rng);
    const gems: Cell[] = [];
    for (const c of gemCand) {
      if (gems.length >= mode.gems) break;
      if (farEnough(c, gems, 2)) gems.push(c);
    }
    for (const c of gemCand) {
      if (gems.length >= mode.gems) break;
      if (!gems.some((o) => o.gx === c.gx && o.gy === c.gy)) gems.push(c);
    }
    m.gems = gems.slice(0, mode.gems);
  }

  return m;
}

/** For tests/sim: a shortest floor path (avoiding a blocked set), or null. */
export function pathBetween(m: Maze, a: Cell, b: Cell, blocked?: Set<number>): Cell[] | null {
  return shortestPath(m, a, b, blocked);
}

/** For tests/sim: floor cells reachable from a cell (optionally avoiding a set). */
export function reachableFrom(m: Maze, from: Cell, blocked?: Set<number>): Set<number> {
  return reachable(m, from, blocked);
}

export const cellIndex = idx;
